/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { makeStyles, RACCard, Typography, CircularProgress } from '@rentacenter/racstrap';
import clsx from 'clsx';
import { getRetailerLaunchOptions } from '../../api/user';
import { ReactComponent as BestBuyIcon } from '../../assets/images/bestbuy.svg';
import { ReactComponent as HomeDepotIcon } from '../../assets/images/home.svg';
import { ReactComponent as WalmartIcon } from '../../assets/images/walmart.svg';
import { useNationalPartnerActions, useNationalPartnerData } from './NationalPartnerProvider';
import { deriveCartUrl } from './navigationHelpers';
import { FundingInProgressWarningModal } from './FundingInProgressWarningModal';

export interface RetailerLaunchOption {
  retailerLaunchOptionId: number;
  retailerCode: string;
  retailerNotes: string;
  displayNameEn: string;
  displayNameEs: string;
  iconUrl: string;
  externalLaunchUrl: string;
  isActive: boolean;
}

// Map retailer codes to local SVG icon components
const RETAILER_ICONS: Record<string, React.FC<any>> = {
  BESTBUY: BestBuyIcon,
  HOMEDEPOT: HomeDepotIcon,
  WALMART: WalmartIcon,
};

const useStyles = makeStyles((theme: any) => ({
  vendorsRoot: {
    width: '100%',
    height: '100%',
    padding: '0 0.5rem 0 0',
    overflow: 'hidden',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  cardBody: {
    flex: '1 1 auto',
    padding: '1.5rem',
    overflow: 'hidden',
  },
  header: {
    fontSize: '1rem',
    marginBottom: '0.75rem',
  },
  vendorGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.typography.pxToRem(20),
    padding: '0.5rem 0',
  },
  vendorItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.typography.pxToRem(70),
    height: theme.typography.pxToRem(70),
    borderRadius: theme.typography.pxToRem(16),
    backgroundColor: '#FFFFFF',
    border: '1px solid #E8E8E8',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
      transform: 'scale(1.03)',
    },
    '&.disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      '&:hover': {
        boxShadow: 'none',
        transform: 'scale(1)',
      },
    },
  },
  vendorIcon: {
    width: '70%',
    height: '70%',
  },
  vendorImg: {
    maxWidth: '70%',
    maxHeight: '70%',
    objectFit: 'contain' as const,
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: theme.typography.pxToRem(120),
  },
  noDataText: {
    color: '#999',
    fontSize: '0.875rem',
  },
}));

export const LocalSourcingVendors = () => {
  const classes = useStyles();
  const [retailers, setRetailers] = useState<RetailerLaunchOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const { setNationalPartnerVendor, setCheckoutFailedAcknowledged } = useNationalPartnerActions();
  const {
    isProcessing,
    fundingState,
    vendorName,
    approvalLimitAmount,
    resolvedProducts,
    checkoutState,
    spendLimitId,
    checkoutFailedAcknowledged,
  } = useNationalPartnerData();

  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        setLoading(true);
        const response = await getRetailerLaunchOptions();
        if (response?.status === 200 && response?.data) {
          const activeRetailers = Array.isArray(response.data)
            ? response.data.filter((r: RetailerLaunchOption) => r.isActive)
            : [];
          setRetailers(activeRetailers);
        }
      } catch (e) {
        console.error('Error fetching retailer launch options:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchRetailers();
  }, []);

  const handleRetailerClick = (retailer: RetailerLaunchOption) => {
    // Don't allow clicking vendor buttons while processing
    if (isProcessing) {
      return;
    }

    // If funding is approved and purchase not yet completed, show warning modal
    if (fundingState === 'APPROVED' && checkoutState !== 'PURCHASE_SUCCESSFUL') {
      if (checkoutState === 'CHECKOUT_FAILED') {
        // Show checkout-failed modal — after OK, vendors become enabled
        if (!checkoutFailedAcknowledged) {
          setShowWarningModal(true);
          return;
        }
        // Already acknowledged — let them click through to vendor
      } else {
        // Normal case: checkout still in progress, block with warning
        setShowWarningModal(true);
        return;
      }
    }

    // Do not open popup if vendor has no configured externalLaunchUrl
    if (!retailer.externalLaunchUrl) {
      return;
    }

    // Set vendor in National Partner context so CapturedImage knows which vendor was selected
    setNationalPartnerVendor(getDisplayName(retailer), retailer.retailerCode);

    // Determine the URL to open: cart URL if funding is approved, otherwise homepage
    const targetUrl =
      fundingState === 'APPROVED'
        ? deriveCartUrl(retailer.externalLaunchUrl)
        : retailer.externalLaunchUrl;

    const width = 1280;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      targetUrl,
      'RetailerWindow',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,scrollbars=yes,resizable=yes`
    );
  };

  const getDisplayName = (retailer: RetailerLaunchOption): string => {
    const language = window.sessionStorage.getItem('language') || 'en';
    if (language === 'es' && retailer.displayNameEs) {
      return retailer.displayNameEs;
    }
    return retailer.displayNameEn;
  };

  const renderIcon = (retailer: RetailerLaunchOption) => {
    // Use iconUrl from API (presigned S3 URL) if available
    if (retailer.iconUrl) {
      return <img src={retailer.iconUrl} alt={getDisplayName(retailer)} className={classes.vendorImg} />;
    }

    // Fallback to local SVG matched by retailerCode
    const LocalIcon = RETAILER_ICONS[retailer.retailerCode] || RETAILER_ICONS[retailer.retailerCode.toUpperCase()];
    if (LocalIcon) {
      return <LocalIcon className={classes.vendorIcon} />;
    }
    return (
      <span style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem' }}>
        {getDisplayName(retailer)}
      </span>
    );
  };

  return (
    <div className={clsx(classes.vendorsRoot)}>
      <RACCard className={classes.card}>
        <div className={clsx(classes.cardBody)}>
          <Typography className={clsx(classes.header)} variant="h6">
            {'National Retailers'}
          </Typography>

          {loading ? (
            <div className={clsx(classes.loaderContainer)}>
              <CircularProgress />
            </div>
          ) : retailers.length === 0 ? (
            <div className={clsx(classes.vendorGrid)}>
              {Object.entries(RETAILER_ICONS).map(([code, IconComponent]) => (
                <div
                  key={code}
                  className={clsx(classes.vendorItem)}
                  role="button"
                  tabIndex={0}
                  aria-label={code}
                >
                  <IconComponent className={classes.vendorIcon} />
                </div>
              ))}
            </div>
          ) : (
            <div className={clsx(classes.vendorGrid)}>
              {retailers.map((retailer) => (
                <div
                  key={retailer.retailerLaunchOptionId}
                  className={clsx(classes.vendorItem, isProcessing && 'disabled')}
                  onClick={() => handleRetailerClick(retailer)}
                  title={getDisplayName(retailer)}
                  role="button"
                  tabIndex={isProcessing ? -1 : 0}
                  aria-label={`Open ${getDisplayName(retailer)}`}
                  aria-disabled={isProcessing}
                  onKeyDown={(e) => {
                    if (!isProcessing && (e.key === 'Enter' || e.key === ' ')) {
                      handleRetailerClick(retailer);
                    }
                  }}
                >
                  {renderIcon(retailer)}
                </div>
              ))}
            </div>
          )}
        </div>
      </RACCard>

      {/* Warning modal when funding is approved and user tries to click a vendor */}
      <FundingInProgressWarningModal
        isOpen={showWarningModal}
        vendorName={vendorName}
        productDescription={
          resolvedProducts && resolvedProducts.length > 0
            ? resolvedProducts
                .map((p) => {
                  return p.description || p.brand || '';
                })
                .filter(Boolean)
                .join(', ')
            : ''
        }
        approvedAmount={approvalLimitAmount}
        onClose={() => setShowWarningModal(false)}
        onOk={() => {
          setShowWarningModal(false);
          if (checkoutState === 'CHECKOUT_FAILED') {
            setCheckoutFailedAcknowledged(true);
          }
        }}
        checkoutFailed={checkoutState === 'CHECKOUT_FAILED'}
        spendLimitId={spendLimitId}
      />
    </div>
  );
};
